import { signOutAndRedirect } from "~/lib/authSession";
import { getCSRFToken, getToken, setToken } from "~/lib/tokenStore";

const TOKEN_REFRESH_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes before expiry
const TOKEN_CHECK_INTERVAL_MS = 2 * 60 * 1000; // check every 2 minutes

let refreshPromise = null;
let checkInterval = null;

const decodeTokenPayload = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

const isTokenExpiringSoon = (token) => {
  if (!token) {
    return true;
  }

  const payload = decodeTokenPayload(token);
  if (!payload?.exp) {
    return false; // no expiry (e.g. document tokens)
  }

  const expiresAtMs = payload.exp * 1000;
  const remainingMs = expiresAtMs - Date.now();
  const expiring = remainingMs < TOKEN_REFRESH_THRESHOLD_MS;

  return expiring;
};

const refreshToken = () => {
  // Deduplicate concurrent refresh requests
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = fetch("/auth/refresh_token", {
    method: "POST",
    headers: {
      "X-CSRF-Token": getCSRFToken(),
      Accept: "application/json",
    },
  })
    .then(async (res) => {
      if (res.status === 401) {
        // Only navigate when the server signalled a specific reauth target
        // (force-logout from AuthenticationConcern#handle_expired_oauth_token).
        // A bare 401 means the refresh session itself is invalid, so send the
        // browser through the normal sign-out path immediately.
        const body = await res.json().catch(() => ({}));
        if (body?.reauth_url) {
          window.top.location = body.reauth_url;
          // Leave the promise pending so in-flight callers unwind with the
          // navigation instead of racing with signOutAndRedirect.
          return new Promise(() => {
            /* intentionally never settles — browser is navigating away */
          });
        }

        signOutAndRedirect({ stopRefresh: stopBackgroundRefresh });
        return new Promise(() => {
          /* intentionally never settles — browser is navigating away */
        });
      }
      if (!res.ok) {
        throw new Error(`Refresh failed (${res.status})`);
      }
      return res.json();
    })
    .then((body) => {
      if (!body?.token) {
        throw new Error("No token in response");
      }
      setToken(body.token);
      return body.token;
    })
    .catch((err) => {
      console.error("[TokenManager] Refresh failed:", err.message);
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const proactiveRefreshCheck = () => {
  const token = getToken();
  // No token means no session to refresh (e.g. anonymous sign-in page) —
  // hitting /auth/refresh_token would just yield a 401 loop.
  if (!token) {
    return;
  }
  if (isTokenExpiringSoon(token)) {
    refreshToken().catch(() => {
      // Refresh failed silently — will be caught by 401 handler on next request
    });
  }
};

const startBackgroundRefresh = () => {
  if (checkInterval) {
    return;
  }

  proactiveRefreshCheck();
  checkInterval = setInterval(proactiveRefreshCheck, TOKEN_CHECK_INTERVAL_MS);
};

const stopBackgroundRefresh = () => {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
};

// Start background refresh when module loads
startBackgroundRefresh();

export {
  refreshToken,
  startBackgroundRefresh,
  stopBackgroundRefresh,
  isTokenExpiringSoon,
};
